/**
 * The pool screen's four tabs live in `?tab=` on the existing `/pools` route.
 *
 * A search param, not four child routes: `apps/app/src/routes/__root.tsx`
 * holds the only `<Outlet />` in the routes tree, so a `pools.members.tsx`
 * child would never paint, and `infra/ci/checkRouteCoverage.ts` would want a
 * browser spec per new route file. The same reasoning
 * `poolMemberDetailRoute.ts` records.
 *
 * `?view=` was the old address for four of these destinations. Links carrying
 * it are already out in the world, in push notification targets and in pasted
 * URLs, so `aliasLegacyPoolView` keeps every one of them resolving. It is not
 * removed until the links carrying it can no longer exist.
 */
import { coerceOptionalEnum } from "../../routeState/searchParams";

export const POOL_TABS = ["members", "settings", "game", "results"] as const;

export type typePoolTab = (typeof POOL_TABS)[number];

/** Matching the legacy app's own first tab. */
export const DEFAULT_POOL_TAB: typePoolTab = "members";

/**
 * The retired `?view=` values, and the address each one now resolves to.
 *
 * `member` is deliberately absent: one member's pool-scoped profile is a
 * drill-down over the Members tab, not a fifth tab, so it keeps its own
 * address and `pools.tsx` still reads it through `isPoolMemberDetailOpen`.
 */
export const LEGACY_POOL_VIEWS = [
  "members",
  "settings",
  "standings",
  "notifications",
] as const;

const LEGACY_VIEW_ALIASES: Readonly<
  Record<
    (typeof LEGACY_POOL_VIEWS)[number],
    { tab: typePoolTab; panel?: string }
  >
> = {
  members: { tab: "members" },
  settings: { tab: "settings" },
  standings: { tab: "results" },
  // The per-pool notifications screen is a panel inside Settings now, so the
  // tab alone is not the whole address.
  notifications: { tab: "settings", panel: "notifications" },
};

export function readPoolTab(search: { tab?: string | undefined }): typePoolTab {
  return coerceOptionalEnum(search.tab, POOL_TABS) ?? DEFAULT_POOL_TAB;
}

/**
 * Moves to another tab, dropping the state that belonged to the tab being
 * left: the retired `view` key, the Settings tab's own `panel` sub-address,
 * and any open sheet. Carrying those onto a different tab would leave state
 * in the URL that nothing on screen can show.
 *
 * `memberQ` is kept on purpose. It is the roster's search text, and R7 says
 * the tab a fan leaves still holds what they typed when they come back.
 */
export function openPoolTabSearch<TSearch extends Record<string, unknown>>(
  search: TSearch,
  tab: typePoolTab,
): TSearch & { tab: typePoolTab } {
  const dropped = new Set<string>(["view", "panel", "sheet"]);
  const kept = Object.fromEntries(
    Object.entries(search).filter(([key]) => !dropped.has(key)),
  ) as TSearch;
  return { ...kept, tab };
}

/**
 * Rewrites a retired `?view=` into the `?tab=` address that replaced it.
 *
 * An explicit `?tab=` wins: a link carrying both is not a legacy link, and
 * the newer key is the one the fan's last navigation wrote.
 */
export function aliasLegacyPoolView(
  search: Record<string, unknown>,
): Record<string, unknown> {
  const view = coerceOptionalEnum(search.view, LEGACY_POOL_VIEWS);
  if (view === undefined) return search;

  const next: Record<string, unknown> = { ...search };
  delete next.view;
  if (coerceOptionalEnum(search.tab, POOL_TABS) === undefined) {
    const alias = LEGACY_VIEW_ALIASES[view];
    next.tab = alias.tab;
    if (alias.panel !== undefined) next.panel = alias.panel;
  }
  return next;
}
